import type { MouseEvent } from 'react';
import type { ColumnsType } from 'antd/es/table';

import { hasCellDataForKpis, KpiCellStack } from './PackArchitectureTableCells';
import { PackArchitectureTableEllipsisText } from './PackArchitectureTableEllipsisText';
import {
    TABLE_SEGMENT_COLUMN_WIDTH_PX,
    TABLE_VENDOR_COLUMN_WIDTH_PX,
} from './packArchitectureTable.helpers';
import type { TableColumn, TableRow } from './packArchitectureTable.helpers';

type BuildTableColumnsParams = {
    visibleColumns: TableColumn[];
    orderedSelectedKpis: string[];
    yAxis: string;
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    hoveredRowKey: string | null;
    hoveredColumnKey: string | null;
    onCellHover: (rowKey: string, columnKey: string | null) => void;
    onColumnHeaderHover: (columnKey: string) => void;
    onCellClick: (segmentKey: string, columnKey: string) => void;
    onColumnHeaderClick: () => void;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

export const getVendorCellClassName = (
    record: TableRow,
    colKey: string,
    orderedSelectedKpis: string[],
    activeSegmentKey: string | null,
    selectedColumnKey: string | null,
    hoveredRowKey: string | null,
    hoveredColumnKey: string | null,
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean,
) => {
    const classes = [
        hasCellDataForKpis(record.cells[colKey], orderedSelectedKpis, matchesKpiId)
            ? 'vendor-cell'
            : 'vendor-cell vendor-cell-empty',
    ];

    if (hoveredRowKey === record.key) {
        classes.push('table-cell-hover-row');
    }
    if (hoveredColumnKey === colKey) {
        classes.push('table-cell-hover-column');
    }
    if (hoveredRowKey === record.key && hoveredColumnKey === colKey) {
        classes.push('table-cell-hover-active');
    }

    if (!activeSegmentKey) return classes.join(' ');

    if (record.key === activeSegmentKey) {
        classes.push('vendor-cell-row-selected');
        if (selectedColumnKey === colKey) {
            classes.push('vendor-cell-selected');
        }
    } else {
        classes.push('vendor-cell-dimmed');
    }

    return classes.join(' ');
};

export const getSegmentCellClassName = (
    record: TableRow,
    activeSegmentKey: string | null,
    hoveredRowKey: string | null,
) => {
    const classes: string[] = [];

    if (hoveredRowKey === record.key) {
        classes.push('table-cell-hover-row');
    }

    if (activeSegmentKey === record.key) {
        classes.push('segment-cell-selected');
    } else if (activeSegmentKey) {
        classes.push('segment-cell-dimmed');
    }

    return classes.length > 0 ? classes.join(' ') : undefined;
};

export const buildPackArchitectureTableColumns = ({
    visibleColumns,
    orderedSelectedKpis,
    yAxis,
    activeSegmentKey,
    selectedColumnKey,
    hoveredRowKey,
    hoveredColumnKey,
    onCellHover,
    onColumnHeaderHover,
    onCellClick,
    onColumnHeaderClick,
    matchesKpiId,
}: BuildTableColumnsParams): ColumnsType<TableRow> => {
    const segmentColumn: ColumnsType<TableRow>[number] = {
        title: '',
        dataIndex: 'label',
        key: 'segment',
        fixed: 'left',
        width: TABLE_SEGMENT_COLUMN_WIDTH_PX,
        align: 'left',
        onHeaderCell: () => ({
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onColumnHeaderClick();
            },
        }),
        onCell: (record: TableRow) => ({
            className: getSegmentCellClassName(record, activeSegmentKey, hoveredRowKey),
            onMouseEnter: () => onCellHover(record.key, null),
        }),
        render: (label: string) => (
            <PackArchitectureTableEllipsisText className="w-full text-left text-xs font-semibold">
                {label}
            </PackArchitectureTableEllipsisText>
        ),
    };

    const vendorColumns: ColumnsType<TableRow> = visibleColumns.map((col) => ({
        title: (
            <PackArchitectureTableEllipsisText className="text-left font-semibold">
                {col.label}
            </PackArchitectureTableEllipsisText>
        ),
        key: col.key,
        align: 'left' as const,
        width: TABLE_VENDOR_COLUMN_WIDTH_PX,
        onHeaderCell: () => ({
            className: hoveredColumnKey === col.key ? 'table-header-hover-column' : undefined,
            onMouseEnter: () => onColumnHeaderHover(col.key),
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onColumnHeaderClick();
            },
        }),
        onCell: (record: TableRow) => ({
            className: getVendorCellClassName(
                record,
                col.key,
                orderedSelectedKpis,
                activeSegmentKey,
                selectedColumnKey,
                hoveredRowKey,
                hoveredColumnKey,
                matchesKpiId,
            ),
            onMouseEnter: () => onCellHover(record.key, col.key),
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onCellClick(record.key, col.key);
            },
        }),
        render: (_: unknown, record: TableRow) => (
            <KpiCellStack
                cell={record.cells[col.key]}
                selectedKpis={orderedSelectedKpis}
                yAxisKpiId={yAxis}
                matchesKpiId={matchesKpiId}
            />
        ),
    }));

    return [segmentColumn, ...vendorColumns];
};
